import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import endpoints from '../../config/endpoints';
import { setLoading } from '../../services/reducers/homeSlice';
import restApi from '../../services/restApi';
import { Sidebar } from '../Layout/Sidebar';
import { PickableUser, UserPicker } from '../shared/UserPicker';

type SmsUser = PickableUser;

// ─── SMS Panel ───────────────────────────────────────────────────────────────

function SmsPanel({ users }: { users: SmsUser[] }) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!message.trim()) {
      Swal.fire({ title: 'خطا', text: 'متن پیام را وارد کنید', icon: 'error', confirmButtonText: 'باشه' });
      return;
    }
    if (selectedIds.size === 0) {
      Swal.fire({ title: 'خطا', text: 'حداقل یک کاربر انتخاب کنید', icon: 'error', confirmButtonText: 'باشه' });
      return;
    }
    const confirm = await Swal.fire({
      title: 'ارسال پیامک',
      text: `پیامک به ${selectedIds.size} کاربر ارسال می‌شود. ادامه می‌دهید؟`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'بله، ارسال کن',
      cancelButtonText: 'انصراف',
    });
    if (!confirm.isConfirmed) return;

    setSending(true);
    const res = await restApi(endpoints.sms.send, true).post({
      userIds: Array.from(selectedIds),
      message: message.trim(),
    });
    setSending(false);

    if (res.code === 200) {
      const { sent, failed } = res.data;
      Swal.fire({
        title: 'ارسال انجام شد',
        html: `<p>ارسال موفق: <b>${sent}</b></p><p>ارسال ناموفق: <b>${failed}</b></p>`,
        icon: 'success',
        confirmButtonText: 'متوجه شدم',
      });
      setMessage('');
      setSelectedIds(new Set());
    } else {
      Swal.fire({ title: 'خطا', text: res?.data || 'خطایی رخ داد', icon: 'error', confirmButtonText: 'متوجه شدم' });
    }
  };

  return (
    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flex: 1 }}>
      <UserPicker users={users} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />

      <div style={{ width: '300px', background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', flexShrink: 0 }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#333' }}>متن پیامک</h3>
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="متن پیامک را اینجا بنویسید..."
          style={{ width: '100%', height: '160px', borderRadius: '8px', border: '1px solid #ddd', padding: '12px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit', direction: 'rtl', boxSizing: 'border-box', outline: 'none' }}
        />
        <div style={{ textAlign: 'left', fontSize: '12px', color: '#aaa', marginTop: '4px' }}>{message.length} کاراکتر</div>
        <div style={{ marginTop: '16px', padding: '12px', background: '#f8f9fa', borderRadius: '8px', fontSize: '13px', color: '#555', lineHeight: '1.8' }}>
          <div>کاربران انتخاب‌شده: <b>{selectedIds.size}</b></div>
          <div>تعداد کل کاربران: <b>{users.length}</b></div>
        </div>
        <button
          onClick={handleSend}
          disabled={sending || selectedIds.size === 0 || !message.trim()}
          style={{ marginTop: '14px', width: '100%', padding: '11px', background: selectedIds.size === 0 || !message.trim() ? '#ccc' : '#4a90e2', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: selectedIds.size === 0 || !message.trim() ? 'not-allowed' : 'pointer', transition: 'background 0.2s' }}
        >
          {sending ? 'در حال ارسال...' : `ارسال پیامک به ${selectedIds.size} کاربر`}
        </button>
      </div>
    </div>
  );
}

// ─── Notification Panel ──────────────────────────────────────────────────────

function NotificationPanel({ users }: { users: SmsUser[] }) {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [sendToAll, setSendToAll] = useState(false);
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!title.trim()) {
      Swal.fire({ title: 'خطا', text: 'عنوان نوتیفیکیشن را وارد کنید', icon: 'error', confirmButtonText: 'باشه' });
      return;
    }
    if (!message.trim()) {
      Swal.fire({ title: 'خطا', text: 'متن نوتیفیکیشن را وارد کنید', icon: 'error', confirmButtonText: 'باشه' });
      return;
    }
    if (!sendToAll && selectedIds.size === 0) {
      Swal.fire({ title: 'خطا', text: 'حداقل یک کاربر انتخاب کنید یا «ارسال به همه» را فعال کنید', icon: 'error', confirmButtonText: 'باشه' });
      return;
    }

    const recipientLabel = sendToAll ? 'همه کاربران' : `${selectedIds.size} کاربر`;
    const confirm = await Swal.fire({
      title: 'ارسال نوتیفیکیشن',
      text: `نوتیفیکیشن به ${recipientLabel} ارسال می‌شود. ادامه می‌دهید؟`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'بله، ارسال کن',
      cancelButtonText: 'انصراف',
    });
    if (!confirm.isConfirmed) return;

    setSending(true);
    const res = await restApi(endpoints.notification.send, true).post({
      userIds: sendToAll ? 'all' : Array.from(selectedIds),
      title: title.trim(),
      message: message.trim(),
    });
    setSending(false);

    if (res.code === 200) {
      Swal.fire({ title: 'ثبت شد', text: 'نوتیفیکیشن ثبت شد و به محض باز کردن اپلیکیشن توسط کاربران نمایش داده می‌شود', icon: 'success', confirmButtonText: 'متوجه شدم' });
      setTitle('');
      setMessage('');
      setSelectedIds(new Set());
      setSendToAll(false);
    } else {
      Swal.fire({ title: 'خطا', text: res?.data || 'خطایی رخ داد', icon: 'error', confirmButtonText: 'متوجه شدم' });
    }
  };

  return (
    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start', flex: 1 }}>
      {!sendToAll && (
        <UserPicker users={users} selectedIds={selectedIds} setSelectedIds={setSelectedIds} />
      )}

      <div style={{ width: sendToAll ? '100%' : '320px', maxWidth: '380px', background: '#fff', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.07)', flexShrink: 0 }}>
        <h3 style={{ margin: '0 0 16px 0', fontSize: '15px', color: '#333' }}>محتوای نوتیفیکیشن</h3>

        {/* Send to all toggle */}
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', cursor: 'pointer', fontSize: '13px', color: '#444' }}>
          <input
            type="checkbox"
            checked={sendToAll}
            onChange={e => { setSendToAll(e.target.checked); setSelectedIds(new Set()); }}
            style={{ width: '16px', height: '16px', cursor: 'pointer' }}
          />
          ارسال به همه کاربران
        </label>

        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="عنوان نوتیفیکیشن..."
          style={{ width: '100%', borderRadius: '8px', border: '1px solid #ddd', padding: '10px 12px', fontSize: '14px', fontFamily: 'inherit', direction: 'rtl', boxSizing: 'border-box', outline: 'none', marginBottom: '10px' }}
        />

        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          placeholder="متن نوتیفیکیشن را اینجا بنویسید..."
          style={{ width: '100%', height: '140px', borderRadius: '8px', border: '1px solid #ddd', padding: '12px', fontSize: '14px', resize: 'vertical', fontFamily: 'inherit', direction: 'rtl', boxSizing: 'border-box', outline: 'none' }}
        />
        <div style={{ textAlign: 'left', fontSize: '12px', color: '#aaa', marginTop: '4px' }}>{message.length} کاراکتر</div>

        <div style={{ marginTop: '14px', padding: '12px', background: '#f0f7ff', borderRadius: '8px', fontSize: '13px', color: '#555', lineHeight: '1.8' }}>
          <div>دریافت‌کنندگان: <b>{sendToAll ? 'همه کاربران' : `${selectedIds.size} نفر`}</b></div>
          <div>تعداد کل کاربران: <b>{users.length}</b></div>
        </div>

        <button
          onClick={handleSend}
          disabled={sending || !title.trim() || !message.trim() || (!sendToAll && selectedIds.size === 0)}
          style={{
            marginTop: '14px', width: '100%', padding: '11px',
            background: (!title.trim() || !message.trim() || (!sendToAll && selectedIds.size === 0)) ? '#ccc' : '#14337D',
            color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold',
            cursor: (!title.trim() || !message.trim() || (!sendToAll && selectedIds.size === 0)) ? 'not-allowed' : 'pointer',
            transition: 'background 0.2s',
          }}
        >
          {sending ? 'در حال ارسال...' : `ارسال نوتیف ${sendToAll ? 'به همه' : `به ${selectedIds.size} کاربر`}`}
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

const SmsAndNotif = () => {
  const dispatch = useDispatch();
  const [users, setUsers] = useState<SmsUser[]>([]);
  const [activeTab, setActiveTab] = useState<'sms' | 'notif'>('sms');

  const fetchUsers = async () => {
    dispatch(setLoading(true));
    const res = await restApi(endpoints.sms.users, true).get();
    if (res.code === 200) setUsers(res.data);
    dispatch(setLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const tabStyle = (tab: 'sms' | 'notif') => ({
    padding: '10px 24px',
    background: activeTab === tab ? (tab === 'sms' ? '#4a90e2' : '#14337D') : '#fff',
    color: activeTab === tab ? '#fff' : '#555',
    border: `1.5px solid ${activeTab === tab ? (tab === 'sms' ? '#4a90e2' : '#14337D') : '#ddd'}`,
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: 600 as const,
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontFamily: 'inherit',
  });

  return (
    <body className="dashboardBody">
      <Sidebar />
      <main className="dashBoardMain">
        <h1 className="dashBoardTitle">مدیریت پیامک و نوتیفیکیشن</h1>

        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '16px', marginBottom: '20px' }}>
          <button style={tabStyle('sms')} onClick={() => setActiveTab('sms')}>
            📱 ارسال پیامک
          </button>
          <button style={tabStyle('notif')} onClick={() => setActiveTab('notif')}>
            🔔 ارسال نوتیفیکیشن
          </button>
        </div>

        {activeTab === 'sms' && <SmsPanel users={users} />}
        {activeTab === 'notif' && <NotificationPanel users={users} />}
      </main>
    </body>
  );
};

export default SmsAndNotif;
